import { useState } from 'react'
import { CommissionGrid, useCommissionPlans, type PlanSubject } from '@/components/CommissionGrid'
import { ErrorState, Loading } from '@/components/ui/primitives'
import type { ResellerDownline } from '@/types/api'

/**
 * What a partner earns: where they depart from the standard, and what a downline's deal pays them.
 *
 * <p>
 * The first grid is <b>custom rates</b>, not the whole of what they earn. What a product pays is
 * a property of the product and lives in the Product module; this is only where this partner is
 * entitled to something different. Anything they hold no row for takes the standard.
 * </p>
 * <p>
 * The override tabs appear only for a partner who holds downlines. A second-level partner has
 * none, so there is no override configuration for them to see - a shape rule rather than a
 * permission.
 * </p>
 */
export function ResellerCommission({
  resellerId,
  countryId,
  downlines,
}: {
  resellerId: string
  countryId: string | undefined
  downlines: readonly ResellerDownline[]
}) {
  /** Null is the partner's own custom grid. A downline id is the override held against them. */
  const [downlineId, setDownlineId] = useState<string | null>(null)

  const plans = useCommissionPlans({ kind: 'reseller', resellerId, downlineId: null })

  if (plans.loading && !plans.data) return <Loading />
  if (plans.error) {
    return <ErrorState title={plans.error.title} detail={plans.error.detail} onRetry={plans.reload} />
  }

  const subject: PlanSubject = { kind: 'reseller', resellerId, downlineId }
  const forTier = (plans.data ?? []).filter(
    (plan) => (plan.downlineResellerId ?? null) === downlineId && plan.kind !== 'ProductStandard',
  )

  const downline = downlines.find((d) => d.id === downlineId)

  return (
    <>
      {downlines.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <TierTab on={downlineId === null} onClick={() => setDownlineId(null)}>
            Custom rates
          </TierTab>
          {downlines.map((d) => (
            <TierTab key={d.id} on={downlineId === d.id} onClick={() => setDownlineId(d.id)}>
              Override · {d.name}
            </TierTab>
          ))}
        </div>
      )}

      <CommissionGrid
        subject={subject}
        countryId={countryId}
        plans={forTier}
        onChanged={plans.reload}
        title={downline ? `Override on ${downline.name}` : 'Custom rates'}
        emptyTitle={downline ? `No override on ${downline.name}` : 'No custom rates'}
        emptyHint={
          downline
            ? `Without one, a deal won by ${downline.name} accrues nothing for this partner - with the reason recorded, but zero.`
            : 'This partner is paid each product’s standard rate. Draft a plan only where they are entitled to something different.'
        }
        editorDescription={
          downline
            ? `What this partner earns when ${downline.name} wins a deal, on top of what ${downline.name} earns for themselves.`
            : 'Where this partner departs from the standard rates. Anything not listed here is paid that product’s standard.'
        }
        footnote={
          downline ? (
            <div className="mb-5 rounded-md border border-line bg-surface-2 px-4 py-3 text-[13px] text-ink-2">
              What this partner earns when {downline.name} wins a deal, <b>on top of</b> what{' '}
              {downline.name} earns for themselves. A downline deal therefore costs Codemax both
              figures, and this grid never falls back to a product&rsquo;s standard rate.
            </div>
          ) : (
            <div className="mb-5 rounded-md border border-line bg-surface-2 px-4 py-3 text-[13px] text-ink-2">
              Only the departures. A product with no row here pays this partner its{' '}
              <b>standard rate</b>, set on the product itself &mdash; so a product added to the
              catalogue next month rates immediately without anybody touching this plan.
            </div>
          )
        }
      />
    </>
  )
}

function TierTab({
  on,
  onClick,
  children,
}: {
  on: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`rounded-sm border px-3 py-1.5 text-[13px] font-medium ${
        on ? 'border-accent bg-accent-wash text-accent' : 'border-line bg-surface text-muted'
      }`}
    >
      {children}
    </button>
  )
}
